import { set_token, set_user_details , clear_token} from "@/router";
import { authFinaleSharedMethods } from "@/views/auth_finale_helper";
import Swal from "sweetalert2";

const PASSWORD_REQUIREMENTS = [
  (password) => password.length >= 8,
  (password) => /[a-z]/.test(password),
  (password) => /[A-Z]/.test(password),
  (password) => /\d/.test(password),
  (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
];

export default {
  inject: ['client_features'],
  data: () => ({
    form: {
      email: "",
      first_name: "",
      last_name: "",
      password: "",
      confirm_password: "",
    },
    errors: {},
    is_submitting: false,
    is_loading_invite: false,
    invite_details: null,
    password_visible: false,
    confirm_password_visible: false,
  }),
  computed: {
    invitation_token() {
      return this.$route.query.invitation_token || this.$route.query.token || this.$route.params.token || "";
    },
    requires_email() {
      return this.invite_details?.requires_email !== false;
    },
  },
  mounted() {
    if (!this.invitation_token) {
      this.errors.form = "Invite token is missing. Please use the full invitation link from your email.";
      return;
    }
    this.fetch_invite_details();
  },
  methods: {
    ...authFinaleSharedMethods,
    fetch_invite_details() {
      this.is_loading_invite = true;
      $.ajax({
        type: "POST",
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_organization_invite_details/`,
        data: JSON.stringify({ invitation_token: this.invitation_token }),
        headers: {
          "Content-Type": "application/json",
        },
        success: (response) => {
          if (response?.status !== "success") {
            this.errors.form = response?.message || "Could not load invite details.";
            return;
          }
          this.invite_details = response;
          if (response.email) {
            this.form.email = response.email;
          }
        },
        error: (xhr) => {
          const data = xhr?.responseJSON || {};
          this.errors.form = data.message || data.detail || "Could not load invite details.";
        },
        complete: () => {
          this.is_loading_invite = false;
        },
      });
    },
    validate_form() {
      const errors = {};
      this.validate_required_profile_fields(errors);
      this.validate_password_fields(errors);
      this.validate_invitation_token(errors);

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    validate_invite_setup_field(field) {
      const errors = {};

      if (field === "password") {
        this.validate_password(errors);
        this.apply_realtime_error("password", errors.password);
        this.validate_invite_setup_field("confirm_password");
        return;
      }

      if (field === "confirm_password") {
        this.validate_confirm_password(errors);
        this.apply_realtime_error("confirm_password", errors.confirm_password);
      }
    },
    apply_realtime_error(field, message) {
      if (field === "password") {
        this.apply_password_realtime_error(message);
        return;
      }

      if (field === "confirm_password") {
        this.apply_confirm_password_realtime_error(message);
      }
    },
    apply_password_realtime_error(message) {
      if (message) {
        this.errors.password = message;
        return;
      }
      this.errors = this.get_realtime_errors("", this.errors.confirm_password);
    },
    apply_confirm_password_realtime_error(message) {
      if (message) {
        this.errors.confirm_password = message;
        return;
      }
      this.errors = this.get_realtime_errors(this.errors.password, "");
    },
    get_realtime_errors(password_error, confirm_password_error) {
      const errors = Object.assign({}, this.errors);
      errors.password = password_error;
      errors.confirm_password = confirm_password_error;
      return errors;
    },
    validate_required_profile_fields(errors) {
      this.add_first_name_error(errors);
      this.add_last_name_error(errors);
      if (this.requires_email) {
        this.add_email_error(errors);
      }
    },
    add_first_name_error(errors) {
      if (this.form.first_name.trim()) return;
      errors.first_name = "First name is required.";
    },
    add_last_name_error(errors) {
      if (this.form.last_name.trim()) return;
      errors.last_name = "Last name is required.";
    },
    add_email_error(errors) {
      if (this.form.email.trim()) return;
      errors.email = "Email is required.";
    },
    validate_password_fields(errors) {
      this.validate_password(errors);
      this.validate_confirm_password(errors);
    },
    validate_password(errors) {
      if (!this.form.password) {
        errors.password = "Password is required.";
        return;
      }
      if (!this.is_valid_password(this.form.password)) {
        errors.password = this.get_credential_requirements_message();
      }
    },
    validate_confirm_password(errors) {
      if (!this.form.confirm_password) {
        errors.confirm_password = "Confirm password is required.";
        return;
      }
      if (this.form.password !== this.form.confirm_password) {
        errors.confirm_password = "Passwords do not match.";
      }
    },
    validate_invitation_token(errors) {
      if (this.invitation_token) return;
      errors.form = "Invite token is missing. Please use the full invitation link from your email.";
    },
    is_valid_password(password) {
      return PASSWORD_REQUIREMENTS.every((requirement) => requirement(password));
    },
    get_credential_requirements_message() {
      return "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.";
    },
    submit_invite_setup() {
      if (!this.validate_form()) return;

      this.is_submitting = true;
      $.ajax({
        type: "POST",
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/complete_organization_invite/`,
        data: JSON.stringify(this.get_invite_setup_payload()),
        headers: this.get_json_headers(),
        success: (response) => {
          this.handle_invite_setup_success(response);
        },
        error: (xhr) => {
          this.handle_invite_setup_error(xhr);
        },
        complete: () => {
          this.is_submitting = false;
        },
      });
    },
    get_invite_setup_payload() {
      const payload = {};
      payload.invitation_token = this.invitation_token;
      if (this.requires_email) payload.email = this.form.email.trim();
      payload.first_name = this.form.first_name.trim();
      payload.last_name = this.form.last_name.trim();
      payload.password = this.form.password;
      payload.confirm_password = this.form.confirm_password;
      return payload;
    },
    get_json_headers() {
      const headers = {};
      headers["Content-Type"] = "application/json";
      return headers;
    },
    handle_invite_setup_success(response) {
      if (response?.status !== "success") {
        this.errors.form = response?.message || "Could not complete invite setup.";
        return;
      }
      clear_token();
      this.persist_invite_setup_session(response);
      this.show_invite_setup_success(response);
    },
    persist_invite_setup_session(response) {
      const response_data = this.get_invite_setup_response_data(response);
      const token = this.get_invite_setup_token(response_data);
      const user_details = this.get_user_details_payload(response_data);
      if (token) set_token(token);
      if (user_details) set_user_details(user_details, { show_organisation: this.client_features?.show_organisation });
      this.set_active_organization_profile(response_data);
    },
    get_invite_setup_response_data(response) {
      return Object.assign({}, response, response?.data || {});
    },
    get_invite_setup_token(response_data) {
      return response_data?.token;
    },
    show_invite_setup_success(response) {
      Swal.fire({
        icon: "success",
        title: "Invite accepted",
        showConfirmButton: false,
        timer: 1200,
        position: "bottom-end",
        toast: true,
      }).then(() => {
        this.$router.push(response?.data?.redirect_url || response?.redirect_url || "/home/");
      });
    },
    handle_invite_setup_error(xhr) {
      const data = xhr?.responseJSON || {};
      this.errors = Object.assign(
        {},
        this.errors,
        this.get_invite_setup_response_errors(data),
        this.get_invite_setup_form_error(data)
      );
    },
    get_invite_setup_response_errors(data) {
      return data.errors || data;
    },
    get_invite_setup_form_error(data) {
      const error = {};
      error.form = [data.message, data.detail].find(Boolean) || "Could not complete invite setup.";
      return error;
    },
  },
};
